import React, { useEffect } from "react";
import { initCloudStorage } from "@telegram-apps/sdk";
import { postEvent } from "@telegram-apps/sdk";
import { retrieveLaunchParams } from "@telegram-apps/sdk";
import Loading from "../LoadingSplashScreen";
import { Ed25519KeyIdentity } from "@dfinity/identity";
import { useRecoilState } from "recoil";
import { authState } from "@/atoms/auth";
import { getSatteliteOptions, setAPICustomIdentity } from "@/api";
import { fetchProfile, profileState } from "@/atoms/profile";
import { User, UserData, getDoc, setDoc } from "@junobuild/core";
import { Profile } from "@/types/entities";
import { useLocation, useNavigate } from "react-router-dom";
import { TonConnectUIProvider, useTonAddress } from "@tonconnect/ui-react";
import authenticateTelegram from "../../api/supabase/authenticate-telegram.ts";
import posthog from "posthog-js";
import { getAPI } from "@/api";
import { createOrUpdateProfile } from "@/atoms/profile";

export default function TelegramAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const IDENITITY_KEY = "telegram_internet_identity";
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [, setUser] = useRecoilState<User | null>(authState);
  const [, setProfile] = useRecoilState<Profile | null>(profileState);

  const location = useLocation();
  const navigate = useNavigate();
  const API = getAPI();
  const rawAddress = useTonAddress(true);

  useEffect(() => {
    async function authorize() {
      console.time("overall authorize");
      const cloudStorage = initCloudStorage();

      const restoredIdentity = await cloudStorage.get(IDENITITY_KEY);
      const { initData, initDataRaw } = retrieveLaunchParams();

      let identity: Ed25519KeyIdentity;
      if (!restoredIdentity) {
        console.log("generating identity");
        identity = await Ed25519KeyIdentity.generate();
        await cloudStorage.set(
          IDENITITY_KEY,
          JSON.stringify(identity.toJSON())
        );
        console.log("idenitity saved", identity.toJSON());
        console.log("principal:", identity.getPrincipal().toString());
      } else {
        identity = Ed25519KeyIdentity.fromJSON(restoredIdentity);
        console.log("restored identity", identity);
        console.log("principal:", identity.getPrincipal().toString());
      }

      setAPICustomIdentity(identity);

      if (initData?.user) {
        posthog.identify(
          "telegram:" + initData?.user?.id, // Replace 'distinct_id' with your user's unique identifier
          {
            username: initData?.user.username,
            firstName: initData?.user.firstName,
            lastName: initData?.user?.lastName,
            language: initData?.user.languageCode,
            isPremium: initData?.user.isPremium,
          } // optional: set additional person properties
        );
      }

      console.time("authenticateTelegram");
      await authenticateTelegram(
        initDataRaw!,
        identity.getPrincipal().toText()
      );
      console.timeEnd("authenticateTelegram");

      console.time("fetchUser");
      const user = await getDoc<UserData>({
        collection: `#user`,
        key: identity.getPrincipal().toText(),
        satellite: getSatteliteOptions(),
      });
      console.timeEnd("fetchUser");

      if (!user) {
        console.time("setDoc with user data");
        const result = await setDoc<UserData>({
          collection: `#user`,
          doc: {
            key: identity.getPrincipal().toText(),
            data: {
              provider: "internet_identity",
            },
          },
          satellite: getSatteliteOptions(),
        });
        console.timeEnd("setDoc with user data");

        console.log("telegram auth: user result", result);

        setUser(result);
      } else {
        console.log("telegram auth: user fetched", user);
        setUser(user);
      }

      console.time("fetchProfile");
      const _profile = await fetchProfile(identity.getPrincipal().toText());
      console.timeEnd("fetchProfile");

      if (_profile) {
        const tonAddress = rawAddress;

        let updatedProfile: Profile | null = null;

        if (!_profile.tonAddress && rawAddress) {
          _profile.tonAddress = tonAddress;
          updatedProfile = {
            ..._profile,
            total_items: _profile.total_items ?? 0,
            avatar_svg: _profile.avatar_svg ?? "",
          };
          console.log("Updating profile with TON address...");
        }

        if (updatedProfile) {
          console.time("createOrUpdateProfile");
          await createOrUpdateProfile(
            identity.getPrincipal().toText(),
            updatedProfile
          );
          setProfile(updatedProfile);
          console.timeEnd("createOrUpdateProfile");
        } else {
          setProfile(_profile);
        }
      }

      if (!_profile?.introduction_completed) {
        console.log("onboarding not completed");
        // navigate("/onboarding");
        navigate("/intro");
      } else {
        // if (location.pathname === "/onboarding") {
        if (location.pathname === "/intro") {
          navigate("/");
        }
      }

      postEvent("web_app_ready");
      postEvent("web_app_expand");
      postEvent("web_app_setup_closing_behavior", { need_confirmation: true });
      postEvent("web_app_request_write_access");

      console.log("initDataRaw", initDataRaw);

      setIsInitialized(true);
      console.timeEnd("overall authorize");
    }

    authorize();
  }, []);

  if (!isInitialized) {
    return <Loading />;
  }

  return (
    <TonConnectUIProvider manifestUrl="https://beta.bridge23.app/tonconnect-manifest.json">
      {children}
    </TonConnectUIProvider>
  );
}
